import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentLoaderService } from './environment-loader.service';
import {
  AccessGroup,
  AccessGroupSearchEvent,
  AccessGroupUserSearchEvent,
  CreateAccessGroup,
  DefaultAccessGroup,
} from 'src/app/features/access-groups/access-group.model';
import { Observable } from 'rxjs';
import { PagedResult } from '../models/paging';
import { ChargingUser } from 'src/app/features/charging-users/charging-user.model';
import { IdTag } from 'src/app/features/id-tags/id-tag-model';
import { AccessGroupCarType } from '../enums/access-group-car-type';
import { OrganizationType } from '../enums/organization-type.enum';

@Injectable({
  providedIn: 'root',
})
export class AccessGroupService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  apiUrl: string;

  constructor() {
    this.apiUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/AccessGroup';
  }

  getAccessGroups(searchEvent: AccessGroupSearchEvent): Observable<PagedResult<AccessGroup>> {
    const url = `${this.apiUrl}`;
    let params = new HttpParams();

    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.chargingUserIdentifier) {
      params = params.append('chargingUserIdentifier', searchEvent.chargingUserIdentifier.toString());
    }
    if (searchEvent?.idTagId) {
      params = params.append('idTagId', searchEvent.idTagId.toString());
    }
    if (searchEvent?.accessGroupIdentifier) {
      params = params.append('accessGroupIdentifier', searchEvent.accessGroupIdentifier.toString());
    }
    if (searchEvent?.chargingStationLocationIdentifier) {
      params = params.append('chargingStationLocationIdentifier', searchEvent.chargingStationLocationIdentifier.toString());
    }
    if (searchEvent?.sharedAccessGroupDiscountIdentifier) {
      params = params.append('sharedAccessGroupDiscountIdentifier', searchEvent.sharedAccessGroupDiscountIdentifier.toString());
    }
    if (searchEvent?.connectorIdentifier) {
      params = params.append('connectorIdentifier', searchEvent.connectorIdentifier.toString());
    }
    if (searchEvent.paging?.pageIndex) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }
    if (searchEvent.paging?.pageSize) {
      params = params.append('pageSize', searchEvent.paging.pageSize);
    }
    if (searchEvent.sorting?.active) {
      params = params.append('sortby', searchEvent.sorting?.active);
    }
    if (searchEvent.sorting?.direction) {
      params = params.append('sortdirection', searchEvent.sorting?.direction);
    }
    if (searchEvent.carType) {
      params = params.append('carType', searchEvent.carType);
    }
    return this.http.get<PagedResult<AccessGroup>>(url, {
      params,
    });
  }

  getAccessGroupUsers(searchEvent: AccessGroupUserSearchEvent): Observable<ChargingUser[]> {
    if (!searchEvent?.accessGroupIdentifier) {
      throw new Error('accessGroupIdentifier is required.');
    }

    const url = `${this.apiUrl}/${searchEvent.accessGroupIdentifier.toString()}/users`;
    let params = new HttpParams();
    if (searchEvent?.chargingUserStatuses) {
      searchEvent?.chargingUserStatuses.forEach(status => {
        if (status) {
          params = params.append('chargingUserStatus', status.toString());
        }
      });
    }
    return this.http.get<ChargingUser[]>(url, {
      params,
    });
  }
  getAccessGroupIdTags(accessGroupIdentifier: string): Observable<IdTag[]> {
    const url = `${this.apiUrl}/${accessGroupIdentifier}/id-tags`;
    return this.http.get<IdTag[]>(url);
  }

  getAccessGroup(accessGroupIdentifier: string): Observable<AccessGroup> {
    const url = `${this.apiUrl}/${accessGroupIdentifier}`;

    return this.http.get<AccessGroup>(url);
  }
  saveAccessGroup(accessGroup: CreateAccessGroup): Observable<string> {
    let url = `${this.apiUrl}`;

    const data = {
      organization_id: accessGroup.organizationId,
      name: accessGroup.name,
      amount: accessGroup.amount,
      connector_identifiers: accessGroup.connectorIdentifiers ?? [],
      car_type: accessGroup.carType,
      shared_discount_identifier: accessGroup.sharedDiscountIdentifier,
    };
    if (accessGroup.identifier) {
      url += `/${accessGroup.identifier}`;
      return this.http.put<string>(url, data);
    } else {
      return this.http.post<string>(url, data);
    }
  }

  saveAccessGroupAccess(
    accessGroupIdentifier: string,
    customerNumbers: number[],
    chargingUserIdentifiers: string[] | null,
    idTagIds: number[] | null
  ): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${accessGroupIdentifier}/access`, {
      customerNumbers: customerNumbers,
      chargingUserIdentifiers: chargingUserIdentifiers,
      idTagIds: idTagIds,
    });
  }

  deleteAccessGroup(identifier: string) {
    const url = `${this.apiUrl}/${identifier}`;
    return this.http.delete(url);
  }

  getDefaultAccessGroups(): Observable<DefaultAccessGroup[]> {
    const url = `${this.apiUrl}/default`;
    return this.http.get<DefaultAccessGroup[]>(url);
  }

  updateDefaultAccessGroup(defaultAccessGroupIdnetifier: string, accessGroup: UpdateDefaultAccessGroupRequest): Observable<void> {
    const url = `${this.apiUrl}/default/${defaultAccessGroupIdnetifier}`;
    return this.http.put<void>(url, accessGroup);
  }

  createDefaultAccessGroup(accessGroup: CreateDefaultAccessGroupRequest): Observable<void> {
    const url = `${this.apiUrl}/default`;
    return this.http.post<void>(url, accessGroup);
  }

  deleteDefaultAccessGroup(identifier: string): Observable<void> {
    const url = `${this.apiUrl}/default/${identifier}`;
    return this.http.delete<void>(url);
  }

  assignStandardAccessGroups(organizationId: number) {
    return this.http.post<void>(`${this.apiUrl}/default/${organizationId}/assign`, {});
  }
}

export interface UpdateDefaultAccessGroupRequest {
  name: string;
  car_type: AccessGroupCarType;
  shared_discount_fixed_identifier: string;
  shared_discount_percentage_identifier: string;
}

export interface CreateDefaultAccessGroupRequest {
  type: OrganizationType;
  name: string;
  shared_discount_fixed_identifier: string;
  shared_discount_percentage_identifier: string;
  car_type: AccessGroupCarType;
}
